import { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  CircleNotchIcon,
  PlusIcon,
  TrashIcon,
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useToast } from 'src/context/ToastContext';
import useTestGlJournalImport from 'src/api/temp/famous/useTestGlJournalImport';
import type {
  GlJournalHeader,
  GlJournalLine,
} from 'src/api/temp/famous/useTestGlJournalImport';

interface JournalLineFormData {
  id: string;
  costCenterId: string;
  phaseId: string;
  departmentId: string;
  accountId: string;
  postingDate: string;
  description: string;
  hours: string;
  quantity: string;
  debit: string;
  credit: string;
}

const createEmptyLine = (): JournalLineFormData => ({
  id: crypto.randomUUID(),
  costCenterId: '',
  phaseId: '',
  departmentId: '',
  accountId: '',
  postingDate: new Date().toISOString().split('T')[0],
  description: '',
  hours: '',
  quantity: '',
  debit: '0',
  credit: '0',
});

export function GlJournalTestCard() {
  const { showToast } = useToast();

  // Header state
  const [header, setHeader] = useState<GlJournalHeader>({
    description: '',
    journalGroup: '',
    accessGroup: '',
    accrualFlag: 'N',
    cashFlag: 'N',
  });

  // Lines state
  const [lines, setLines] = useState<JournalLineFormData[]>([
    createEmptyLine(),
    createEmptyLine(),
  ]);

  // UI state
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    payload?: string;
    errorFileContent?: string;
    totalDebit?: string;
    totalCredit?: string;
  } | null>(null);

  const { mutate: testImport, isPending } = useTestGlJournalImport();

  const handleHeaderChange = (field: keyof GlJournalHeader, value: string) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (
    lineId: string,
    field: keyof JournalLineFormData,
    value: string,
  ) => {
    setLines((prev) =>
      prev.map((line) =>
        line.id === lineId ? { ...line, [field]: value } : line,
      ),
    );
  };

  const addLine = () => {
    setLines((prev) => [...prev, createEmptyLine()]);
  };

  const removeLine = (lineId: string) => {
    setLines((prev) => prev.filter((line) => line.id !== lineId));
  };

  const handleSubmit = () => {
    setResult(null);

    const formattedLines: GlJournalLine[] = lines.map((line) => ({
      costCenterId: line.costCenterId || undefined,
      phaseId: line.phaseId || undefined,
      departmentId: line.departmentId || undefined,
      accountId: line.accountId || undefined,
      postingDate: line.postingDate,
      description: line.description || undefined,
      hours: line.hours ? parseFloat(line.hours) : undefined,
      quantity: line.quantity ? parseFloat(line.quantity) : undefined,
      debit: parseFloat(line.debit) || 0,
      credit: parseFloat(line.credit) || 0,
    }));

    testImport(
      {
        header,
        lines: formattedLines,
        dryRun,
      },
      {
        onSuccess: (response) => {
          setResult({
            success: response.data.success,
            message: response.data.message,
            payload: response.data.payload,
            errorFileContent: response.data.errorFileContent,
            totalDebit: response.data.totalDebit,
            totalCredit: response.data.totalCredit,
          });
          if (response.data.success) {
            showToast({
              title: dryRun ? 'Validation Passed' : 'Import Successful',
              message: response.data.message,
              color: 'success',
              icon: 'check',
            });
          }
        },
        onError: (error) => {
          setResult({
            success: false,
            message: error.message || 'An error occurred',
          });
        },
      },
    );
  };

  // Calculate totals for display
  const totalDebit = lines.reduce(
    (sum, line) => sum + (parseFloat(line.debit) || 0),
    0,
  );
  const totalCredit = lines.reduce(
    (sum, line) => sum + (parseFloat(line.credit) || 0),
    0,
  );
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <WarningIcon className="h-5 w-5 text-amber-500" />
          GL Journal Import Test
        </CardTitle>
        <CardDescription>
          Test FAPI GL Journal imports. Use dry run mode to validate without
          sending to Famous.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warning */}
        <AlertContainer variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertDescription>
            <strong>Admin Tool:</strong> Non-dry-run submissions will create
            actual journal entries in Famous. Use with caution.
          </AlertDescription>
        </AlertContainer>

        {/* Header Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Journal Header</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="journalGroup">Journal Group *</Label>
              <Input
                id="journalGroup"
                placeholder="Enter journal group"
                value={header.journalGroup}
                onChange={(e) =>
                  handleHeaderChange('journalGroup', e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Max 40 characters"
                maxLength={40}
                value={header.description}
                onChange={(e) =>
                  handleHeaderChange('description', e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accessGroup">Access Group</Label>
              <Input
                id="accessGroup"
                placeholder="Optional"
                value={header.accessGroup}
                onChange={(e) =>
                  handleHeaderChange('accessGroup', e.target.value)
                }
              />
            </div>
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>Accrual Flag</Label>
                <Select
                  value={header.accrualFlag}
                  onValueChange={(value: 'Y' | 'N') =>
                    handleHeaderChange('accrualFlag', value)
                  }
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Y">Yes</SelectItem>
                    <SelectItem value="N">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cash Flag</Label>
                <Select
                  value={header.cashFlag}
                  onValueChange={(value: 'Y' | 'N') =>
                    handleHeaderChange('cashFlag', value)
                  }
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Y">Yes</SelectItem>
                    <SelectItem value="N">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Lines Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Journal Lines</h4>
            <Button variant="outline" size="sm" onClick={addLine}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          </div>

          {lines.map((line, index) => (
            <div
              key={line.id}
              className="border rounded-lg p-4 space-y-3 bg-gray-50/50"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Line {index + 1}
                </span>
                {lines.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(line.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Cost Center</Label>
                  <Input
                    placeholder="12 chars max"
                    maxLength={12}
                    value={line.costCenterId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'costCenterId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phase</Label>
                  <Input
                    placeholder="6 chars max"
                    maxLength={6}
                    value={line.phaseId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'phaseId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Department</Label>
                  <Input
                    placeholder="6 chars max"
                    maxLength={6}
                    value={line.departmentId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'departmentId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Account</Label>
                  <Input
                    placeholder="12 chars max"
                    maxLength={12}
                    value={line.accountId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'accountId', e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Posting Date *</Label>
                  <Input
                    type="date"
                    value={line.postingDate}
                    onChange={(e) =>
                      handleLineChange(line.id, 'postingDate', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    placeholder="40 chars max"
                    maxLength={40}
                    value={line.description}
                    onChange={(e) =>
                      handleLineChange(line.id, 'description', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hours</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={line.hours}
                    onChange={(e) =>
                      handleLineChange(line.id, 'hours', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Debit *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={line.debit}
                    onChange={(e) =>
                      handleLineChange(line.id, 'debit', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Credit *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={line.credit}
                    onChange={(e) =>
                      handleLineChange(line.id, 'credit', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Totals */}
          <div className="flex items-center justify-end gap-6 text-sm">
            <span>
              Total Debit: <strong>${totalDebit.toFixed(2)}</strong>
            </span>
            <span>
              Total Credit: <strong>${totalCredit.toFixed(2)}</strong>
            </span>
            <span className={isBalanced ? 'text-green-600' : 'text-red-600'}>
              {isBalanced ? (
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-4 w-4" /> Balanced
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircleIcon className="h-4 w-4" /> Not Balanced
                </span>
              )}
            </span>
          </div>
        </div>

        <Separator />

        {/* Submit Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch id="dryRun" checked={dryRun} onCheckedChange={setDryRun} />
            <Label htmlFor="dryRun" className="text-sm">
              Dry Run (validate only)
            </Label>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !header.journalGroup || !isBalanced}
            variant={dryRun ? 'default' : 'destructive'}
          >
            {isPending ? (
              <>
                <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : dryRun ? (
              'Validate Journal'
            ) : (
              'Submit to Famous'
            )}
          </Button>
        </div>

        {/* Result Section */}
        {result && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Result</h4>
              <AlertContainer
                variant={result.success ? 'default' : 'destructive'}
              >
                {result.success ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
                <AlertDescription>{result.message}</AlertDescription>
              </AlertContainer>

              {result.totalDebit && result.totalCredit && (
                <p className="text-sm text-gray-600">
                  Total Debit: ${result.totalDebit} | Total Credit: $
                  {result.totalCredit}
                </p>
              )}

              {result.errorFileContent && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">
                    Error Details from Famous:
                  </p>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                    {result.errorFileContent}
                  </pre>
                </div>
              )}

              {result.payload && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Generated Payload:</p>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                    {result.payload}
                  </pre>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
